const options = {
	method: 'GET',
	headers: {
		'Accept-Language': Shopify.locale || 'de',
		'accept': 'application/json',
	},
};

class ProductReviewsIoDataLoader {
	constructor({ storeID, SKUs, reviewsCountPerPage, minRating }) {
		this.storeID = storeID;
		this.SKUs = SKUs;
		this.reviewsCountPerPage = reviewsCountPerPage;
		this.minRating = minRating;
		this._summary = null;
		this._phase = 'images';
		this._imageApiPage = 1;
		this._normalApiPage = 1;
		this._seenIds = new Set();
		this._pageCache = new Map();
		this._currentPageIndex = 0;
		this._exhausted = false;
		this._photoReviews = undefined; // undefined = not yet fetched; [] = fetched but none found
	}
	get summary() {
		if (!this._summary) {
			return this.fetchSummary();
		}
		return this._summary;
	}
	async fetchSummary() {
		const queryParameters = new URLSearchParams({
			store: this.storeID,
			sku: this.SKUs,
			type: 'product_review',
			per_page: 0, // we only want the summary data, not the reviews
			include_subrating_breakdown: 1, // needed for the attributes data in the summary
			lang: Shopify.locale || 'de',
		});
		const summaryData = await fetch(`https://api.reviews.co.uk/reviews?${queryParameters.toString()}`, options)
			.then((response) => response.json())
			.catch((error) => {
				console.error('Error:', error);
				return null;
			});
		this._summary = {
			reviewsCount: summaryData?.review_count,
			averageRating: summaryData?.average_rating,
			ratingsStats: summaryData?.stats?.ratings,
			attributes: summaryData?.stats?.attributes,
		};
		return this._summary;
	}

	get currentPage() {
		return this._currentPageIndex + 1;
	}

	get hasNextPage() {
		return !this._exhausted || this._pageCache.has(this._currentPageIndex + 1);
	}

	// ─── Pagination public API ───────────────────────────────────────────────

	async getReviews() {
		const preFetched = this._pageCache.size > 0;

		if (!preFetched) {
			this._phase = 'images';
			this._imageApiPage = 1;
			this._seenIds = new Set();
		}

		this._currentPageIndex = 0;
		this._exhausted = false;
		this._normalApiPage = 1;

		if (this._pageCache.has(0)) {
			return { reviews: this._pageCache.get(0) };
		}

		const reviews = await this._buildPage();
		if (reviews === null) return null;
		this._pageCache.set(0, reviews);
		return { reviews };
	}

	async nextPage() {
		if (this._exhausted && !this._pageCache.has(this._currentPageIndex + 1)) return null;
		this._currentPageIndex++;
		if (this._pageCache.has(this._currentPageIndex)) {
			return { reviews: this._pageCache.get(this._currentPageIndex) };
		}
		const reviews = await this._buildPage();
		if (reviews === null) {
			this._currentPageIndex--;
			return null;
		}
		this._pageCache.set(this._currentPageIndex, reviews);
		return { reviews };
	}

	prevPage() {
		if (this._currentPageIndex === 0) return null;
		this._currentPageIndex--;
		return { reviews: this._pageCache.get(this._currentPageIndex) };
	}

	// ─── Pagination internals ────────────────────────────────────────────────

	async _buildPage() {
		if (this._phase === 'images') {
			const imageReviews = await this._fetchReviewsPage({ page: this._imageApiPage, withImages: true });
			if (imageReviews === null) return null;
			imageReviews.forEach((r) => this._seenIds.add(r.id));
			this._imageApiPage++;

			if (imageReviews.length >= this.reviewsCountPerPage) {
				return imageReviews;
			}

			// Images exhausted — switch to normal phase and pad the remainder
			this._phase = 'normal';
			const needed = this.reviewsCountPerPage - imageReviews.length;
			const normalReviews = await this._fillNormal(needed);
			if (normalReviews === null) return null;

			const merged = [...imageReviews, ...normalReviews];
			if (merged.length < this.reviewsCountPerPage) this._exhausted = true;
			return merged;
		}

		// Normal phase
		const reviews = await this._fillNormal(this.reviewsCountPerPage);
		if (reviews === null) return null;
		if (reviews.length < this.reviewsCountPerPage) this._exhausted = true;
		return reviews;
	}

	async _fillNormal(needed) {
		const temp = [];
		while (temp.length < needed) {
			const page = await this._fetchReviewsPage({ page: this._normalApiPage, withImages: false });
			if (page === null) return null;
			if (page.length === 0) break;
			const nonDupes = page.filter((r) => !this._seenIds.has(r.id));
			nonDupes.forEach((r) => this._seenIds.add(r.id));
			temp.push(...nonDupes);
			this._normalApiPage++;
		}
		return temp.slice(0, needed);
	}

	async fetchPhotoReviews(count = 15) {
		if (this._photoReviews !== undefined) return this._photoReviews;

		const pagesNeeded = Math.ceil(count / this.reviewsCountPerPage);
		let pageIndex = 0;
		const allReviews = [];

		while (pageIndex < pagesNeeded) {
			const batch = await this._fetchReviewsPage({ page: this._imageApiPage, withImages: true });

			if (batch === null) break;

			if (batch.length === 0) {
				this._phase = 'normal';
				this._exhausted = true;
				break;
			}

			batch.forEach((r) => this._seenIds.add(r.id));
			this._imageApiPage++;
			allReviews.push(...batch);

			if (batch.length >= this.reviewsCountPerPage) {
				this._pageCache.set(pageIndex, batch);
				pageIndex++;
			} else {
				// Partial last image page — switch phase, don't cache
				this._phase = 'normal';
				break;
			}
		}

		this._photoReviews = allReviews.filter((r) => r.photos?.length).slice(0, count);
		return this._photoReviews;
	}

	async _fetchReviewsPage({ page, withImages }) {
		// https://developer.reviews.io/reference/899f58c2c4431ec1000ec59a38b2916b
		// type: store_review, product_review, store_third_party_review or questions.
		// with: images, videos or media
		const paramsObj = {
			store: this.storeID,
			type: 'product_review',
			sort: 'highest_rating',
			per_page: this.reviewsCountPerPage,
			page,
			lang: Shopify.locale || 'de',
			must_have_body: true, // makes the API to slow.
		};
		if (this.SKUs) paramsObj.sku = this.SKUs;
		if (withImages) paramsObj.with = 'images';
		if (this.minRating) paramsObj.minRating = this.minRating;

		const queryParameters = new URLSearchParams(paramsObj);
		const data = await fetch(`https://api.reviews.co.uk/reviews?${queryParameters.toString()}`, options)
			.then((response) => response.json())
			.catch((error) => {
				console.error('Error fetching reviews:', error);
				return null;
			});
		return data?.reviews ?? null;
	}
}
export { ProductReviewsIoDataLoader };
